import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Kept for the rare legacy fetch site that hasn't migrated yet; preferred auth
// transport is now the httpOnly `auth_token` cookie set by the backend on login.
export const TOKEN_KEY = "odrsys_token";

const api = axios.create({
    baseURL: API,
    withCredentials: true, // send/receive httpOnly auth_token cookie
});

api.interceptors.request.use((config) => {
    // Fallback Bearer header so existing tooling and any non-browser callers
    // continue to work even if cookies aren't available.
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            if (
                typeof window !== "undefined" &&
                !window.location.pathname.startsWith("/login")
            ) {
                window.location.href = "/login";
            }
        }
        return Promise.reject(err);
    },
);

/**
 * Convert any error shape returned by FastAPI (or thrown by axios) into a
 * single human-readable string. Robust to:
 *   • plain string detail
 *   • {detail: "..."} or {detail: {msg, error, dependencies, ...}}
 *   • {detail: [{loc, msg}, ...]} (pydantic validation arrays)
 *   • {error, dependencies, ack_required, message} (our Round-17 deps payload)
 *   • Round-23 — never returns the literal "[object Object]". If all heuristics
 *     fail we fall back to a JSON serialization of the input so the operator
 *     at least sees structured fields rather than a useless string cast.
 */
export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string"
                ? (e.loc ? `${e.loc.join(".")}: ${e.msg}` : e.msg)
                : (typeof e === "string" ? e : null)))
            .filter(Boolean)
            .join("; ") || "Validation failed.";
    if (typeof detail === "object") {
        // Round-17 dependency-block payload
        if (detail.message && typeof detail.message === "string") return detail.message;
        if (detail.msg && typeof detail.msg === "string") return detail.msg;
        if (detail.error && typeof detail.error === "string") {
            const extra = detail.ack_required ? ` (need to type: ${detail.ack_required})` : "";
            return detail.error + extra;
        }
        if (detail.detail) return formatApiError(detail.detail);
        try {
            return JSON.stringify(detail);
        } catch {
            return "An unexpected error occurred.";
        }
    }
    return String(detail);
}

/**
 * Single-call helper: hand it an exception caught from axios (or anywhere)
 * and get back a clean human-readable error string. Use everywhere instead
 * of `err?.response?.data?.detail || err.message` boilerplate — that
 * boilerplate is what produced "[object Object]" errors when `.detail` was
 * a dict.
 */
export function extractErrorMessage(err) {
    if (!err) return "Unknown error.";
    if (typeof err === "string") return err;
    const detail = err?.response?.data?.detail ?? err?.response?.data;
    if (detail !== undefined && detail !== null) return formatApiError(detail);
    if (err.message) return err.message;
    return formatApiError(err);
}

/**
 * Authenticated blob download. Uses axios (cookie + optional Bearer) instead
 * of raw `fetch` + manual `localStorage.getItem`, which keeps every download
 * call site honest about XSS surface area.
 *
 * @param {string} path  API path beginning with "/"
 * @param {string} fallbackFilename  download name if server didn't send one
 */
export async function downloadFromApi(path, fallbackFilename = "download.pdf") {
    const resp = await api.get(path, { responseType: "blob" });
    const blob = resp.data;

    let filename = fallbackFilename;
    const cd = resp.headers?.["content-disposition"];
    if (cd) {
        const m = /filename="([^"]+)"/.exec(cd);
        if (m) filename = m[1];
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { filename };
}

export default api;
